import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { EditOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { Button, Input, message, Select, Upload, Form, Tooltip } from "antd";
import GenericPageManager, {
  RoleLockTooltip,
} from "../../../Components/PageManager/GenericPageManager";
import logout from "../../../Assets/Images/logout.svg";
import logoutIcon from "../../../Assets/Images/logoutIcon.svg";
import GenericModalForm from "../../../Components/Modals/GenericModalForm";
import pdfFile from "../../../Assets/Files/file-sample.pdf";
import docxFile from "../../../Assets/Files/file-sample.docx";
import { UserContext } from "../../../App";
import {
  handleLogout as clearUserSession,
  handleUpdateUser,
  getRefreshToken,
} from "../../../Utlis/UpdateUserState";
import { logout as logoutApi } from "../../../API/auth";
import {
  getManagerProfile,
  updateManagerProfile,
  updatePageAccess,
  updateManagerAvatar,
  changeManagerPassword,
} from "../../../API/FleetManager/MyProfile";
import InitialsAvatar from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const ROLE_BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const ROLE_BADGE_BASE_STYLE = {
  padding: "5.5px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 500,
  display: "inline-block",
};

const getRoleBadgeStyle = (role) => {
  if (!role || role === "-") {
    return {
      ...ROLE_BADGE_BASE_STYLE,
      background: "#f4f4f5",
      color: "#71717a",
    };
  }
  return {
    ...ROLE_BADGE_BASE_STYLE,
    ...ROLE_BADGE_COLORS[hashString(role) % ROLE_BADGE_COLORS.length],
  };
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const MyProfilePage = ({ setUser }) => {
  const navigate = useNavigate();
  const { userData, setUserData, setUserToken } = useContext(UserContext);
  const [passwordForm] = Form.useForm();

  const location = useLocation();

  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    role: "",
    avatar: null,
  });

  useEffect(() => {
    if (location.state?.updatedUser) {
      setUserInfo((prev) => ({
        ...prev,
        ...location.state.updatedUser,
      }));
    }
  }, [location.state]);

  const [tempUser, setTempUser] = useState(userInfo);

  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [logoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [pageAccess, setPageAccess] = useState({});
  const pagesList = Object.keys(pageAccess);

  // Role limits from the backend: pages the user's role restricts can't be
  // changed, and pages the role only grants View on can't go to View & Edit.
  const [disabledPages, setDisabledPages] = useState([]);
  const [viewOnlyPages, setViewOnlyPages] = useState([]);

  const applyRoleLimits = (data) => {
    if (!data?.disabled_pages) return;
    setDisabledPages(data.disabled_pages.map(formatPageName));
    setViewOnlyPages((data.view_only_pages || []).map(formatPageName));
  };

  const toSlug = (str) =>
    str
      ?.toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-");

  const formatPageName = (key) =>
    key
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const currentRole = userInfo.role
    ? { label: userInfo.role, className: toSlug(String(userInfo.role)) }
    : { label: "Undefined Role", className: "undefined" };

  useEffect(() => {
    if (!userData?.id) return;

    const fetchManager = async () => {
      try {
        setLoadingProfile(true);
        const response = await getManagerProfile();

        if (Math.floor(response?.status / 100) === 2) {
          const data = response.data?.data || {};

          setUserInfo({
            name: data.name,
            email: data.email,
            role: data.role,
            avatar: resolveAvatarUrl(data.avatar),
          });

          applyRoleLimits(data);
          const rawPageAccess = data.page_access || {};
          setPageAccess(
            Object.fromEntries(
              Object.entries(rawPageAccess).map(([key, value]) => [
                formatPageName(key),
                value,
              ]),
            ),
          );
        } else {
          message.error(
            response?.data?.message || "Unexpected response from server.",
          );
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to fetch profile.",
        );
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchManager();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const handlePageAccessChange = async (newPageAccess) => {
    const previousPageAccess = pageAccess;
    setPageAccess(newPageAccess);

    if (!userData?.id) return;

    const rawPageAccess = Object.fromEntries(
      Object.entries(newPageAccess).map(([key, value]) => [toSlug(key), value]),
    );

    try {
      const response = await updatePageAccess(userData.id, rawPageAccess);
      if (Math.floor(response?.status / 100) === 2) {
        applyRoleLimits(response?.data?.data);
        handleUpdateUser(setUserData, {
          ...userData,
          page_access: rawPageAccess,
        });
        message.success(
          response?.data?.message || "Page access updated successfully",
        );
      } else {
        setPageAccess(previousPageAccess);
        message.error(
          response?.data?.message || "Unexpected response from server.",
        );
      }
    } catch (err) {
      setPageAccess(previousPageAccess);
      message.error(
        err?.response?.data?.page_access
          ? "Page access can't exceed what the user's role allows."
          : err?.response?.data?.message || "Failed to update page access.",
      );
    }
  };

  const handleImageUpload = async (file) => {
    const previewUrl = URL.createObjectURL(file);

    setUserInfo((prev) => ({
      ...prev,
      avatar: previewUrl,
    }));

    if (!userData?.id) return false;

    try {
      const response = await updateManagerAvatar(file);

      if (Math.floor(response?.status / 100) === 2) {
        const profile = response.data?.data;
        const resolvedAvatar = resolveAvatarUrl(profile?.avatar) || previewUrl;

        setUserInfo((prev) => ({
          ...prev,
          avatar: resolvedAvatar,
        }));
        handleUpdateUser(setUserData, {
          ...userData,
          avatar: resolvedAvatar,
        });
        message.success(
          response?.data?.message || "Avatar updated successfully",
        );
      } else {
        message.error(
          response?.data?.message || "Unexpected response from server.",
        );
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update avatar.");
    }

    return false;
  };

  const handleEditPersonal = () => {
    setTempUser(userInfo);
    setIsEditingPersonal(true);
  };

  const handleSavePersonal = async () => {
    if (!tempUser.name?.trim()) {
      message.error("Please enter your full name.");
      return;
    }

    if (tempUser.name === userInfo.name) {
      setIsEditingPersonal(false);
      return;
    }

    setSavingPersonal(true);
    try {
      const response = await updateManagerProfile({
        name: tempUser.name,
      });

      if (Math.floor(response?.status / 100) === 2) {
        const resolvedRole = response?.data?.data?.role || userInfo.role;
        const updatedPageAccess = response?.data?.data?.page_access;

        if (updatedPageAccess) {
          setPageAccess(
            Object.fromEntries(
              Object.entries(updatedPageAccess).map(([key, value]) => [
                formatPageName(key),
                value,
              ]),
            ),
          );
        }

        setUserInfo((prev) => ({
          ...prev,
          name: tempUser.name,
          role: resolvedRole,
        }));
        handleUpdateUser(setUserData, {
          ...userData,
          name: tempUser.name,
          role: resolvedRole,
          ...(updatedPageAccess ? { page_access: updatedPageAccess } : {}),
        });
        setIsEditingPersonal(false);
        message.success(
          response?.data?.message || "Profile updated successfully",
        );
      } else {
        message.error(
          getErrorMessage(response?.data, "Unexpected response from server."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update profile."),
      );
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleCancelPersonal = () => {
    setTempUser(userInfo);
    setIsEditingPersonal(false);
  };

  const handleEditPassword = () => {
    setIsEditingPassword(true);
  };

  const handleSavePassword = async () => {
    let values;
    try {
      values = await passwordForm.validateFields();
    } catch (error) {
      return;
    }

    setSavingPassword(true);
    try {
      const response = await changeManagerPassword({
        new_password: values.password,
        confirm_password: values.confirmPassword,
      });

      if (Math.floor(response?.status / 100) === 2) {
        message.success(
          response?.data?.message || "Password changed successfully",
        );
        setIsEditingPassword(false);
        passwordForm.resetFields();
      } else {
        message.error(
          getErrorMessage(response?.data, "Unexpected response from server."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to change password."),
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCancelPassword = () => {
    passwordForm.resetFields();
    setIsEditingPassword(false);
  };

  const handleLogout = async () => {
    try {
      await logoutApi(getRefreshToken());
    } catch (err) {
      console.log(err);
    }
    clearUserSession(setUserData, setUserToken);
    message.success("Logged out successfully");
    setUser("public_manager");
    setIsLogoutModalVisible(false);
    navigate("/selection");
  };

  return (
    <div className="my-profile">
      <div className="header-wrapper">
        <div className="title">My Profile</div>
      </div>

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              <Upload
                showUploadList={false}
                beforeUpload={handleImageUpload}
                accept="image/png, image/jpeg, image/jpg"
              >
                <Button size="small" className="upload-btn">
                  Upload Photo
                </Button>
              </Upload>
              <div className="avatar-wrapper">
                {userInfo.avatar ? (
                  <img
                    src={userInfo.avatar}
                    className="profile-avatar"
                    alt="User"
                  />
                ) : (
                  <InitialsAvatar
                    name={userInfo.name}
                    className="profile-avatar"
                    style={{ fontSize: 40 }}
                  />
                )}
              </div>
            </div>
            <div className="profile-body">
              <div className="profile-name">{userInfo.name}</div>
              <div className="profile-email">{userInfo.email}</div>
              {currentRole.label === "-" ? (
                currentRole.label
              ) : (
                <div style={getRoleBadgeStyle(currentRole.label)}>
                  {currentRole.label}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="profile-info-card">
            <div className="info-title">Personal Information</div>
            <Form layout="vertical">
              <div className="info-grid">
                <Form.Item label="Full Name" style={{ gridColumn: "1 / -1" }}>
                  <Input
                    className="form-field-input"
                    placeholder="Enter Full Name"
                    prefix={<UserOutlined />}
                    value={isEditingPersonal ? tempUser.name : userInfo.name}
                    disabled={!isEditingPersonal}
                    onChange={(e) =>
                      setTempUser({ ...tempUser, name: e.target.value })
                    }
                  />
                </Form.Item>
                <Form.Item label="Role" style={{ gridColumn: "1 / -1" }}>
                  <Tooltip
                    placement="topLeft"
                    color="#ffffff"
                    classNames={{ root: "role-lock-tooltip" }}
                    title={
                      <RoleLockTooltip
                        variant="restricted"
                        title="Role can't be changed here"
                        text="Your role is assigned by an admin and controls your page access, so it can't be edited from your profile."
                      />
                    }
                  >
                    {/* Wrapper so the tooltip still fires on the disabled Select. */}
                    <div style={{ cursor: "not-allowed" }}>
                      <Select
                        className="form-field-input"
                        value={userInfo.role}
                        disabled
                      />
                    </div>
                  </Tooltip>
                </Form.Item>
              </div>
            </Form>

            <div className="edit-btn-wrapper">
              {!isEditingPersonal ? (
                <Button onClick={handleEditPersonal} icon={<EditOutlined />}>
                  Edit Details
                </Button>
              ) : (
                <div className="edit-actions">
                  <Button className="cancel-btn" onClick={handleCancelPersonal}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    className="save-btn"
                    loading={savingPersonal}
                    onClick={handleSavePersonal}
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="profile-info-card password-fields">
        <div className="top-gradient"></div>
        <div className="password-padding">
          <div className="info-title">Change Password</div>
          <Form form={passwordForm} layout="vertical">
            <div className="info-grid">
              <Form.Item
                label={isEditingPassword ? "New Password" : "Password"}
                name="password"
                rules={[
                  {
                    required: isEditingPassword,
                    message: "Please enter password",
                  },
                ]}
              >
                <Input.Password
                  className="form-field-input"
                  placeholder="Enter password"
                  disabled={!isEditingPassword}
                  prefix={<LockOutlined />}
                />
              </Form.Item>

              <Form.Item
                label={
                  isEditingPassword
                    ? "Confirm New Password"
                    : "Confirm Password"
                }
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  {
                    required: isEditingPassword,
                    message: "Please confirm password",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value)
                        return Promise.resolve();
                      return Promise.reject(
                        new Error("Passwords do not match"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  className="form-field-input"
                  placeholder="Confirm password"
                  disabled={!isEditingPassword}
                  prefix={<LockOutlined />}
                />
              </Form.Item>
            </div>
          </Form>

          <div className="edit-btn-wrapper">
            {!isEditingPassword ? (
              <Button onClick={handleEditPassword} icon={<EditOutlined />}>
                Edit Password
              </Button>
            ) : (
              <div className="edit-actions">
                <Button className="cancel-btn" onClick={handleCancelPassword}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  className="save-btn"
                  loading={savingPassword}
                  onClick={handleSavePassword}
                >
                  Update Password
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <GenericPageManager
          pagesList={pagesList}
          pageAccess={pageAccess}
          setPageAccess={handlePageAccessChange}
          childTitle="Log Out"
          childSubTitle="Are you sure you want to logout?"
          childButtonContent="Logout"
          childButtonIcon={<img src={logout} alt="Logout Icon" />}
          onChildButtonPress={() => setIsLogoutModalVisible(true)}
          isCollapsible={true}
          disabledPages={disabledPages}
          viewOnlyPages={viewOnlyPages}
        />

        <GenericModalForm
          open={logoutModalVisible}
          icon={logoutIcon}
          iconBg="#E9E7FB"
          title="Log out?"
          subtitle="Are you sure you want to log out?"
          okText="Sure"
          cancelText="Cancel"
          onCancel={() => setIsLogoutModalVisible(false)}
          onOk={handleLogout}
        />
      </div>
    </div>
  );
};

export default MyProfilePage;
